import { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import { TrendingUp, BarChart3, Calendar } from "lucide-react";
import medicalDB from "../services/database";

const RevenueChart = ({ period = "daily", chartType = "line" }) => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [periodLabel, setPeriodLabel] = useState("");

  useEffect(() => {
    loadRevenueData();
  }, [period, chartType]);

  const loadRevenueData = async () => {
    try {
      setLoading(true);
      let data = [];
      let total = 0;
      let label = "";

      const now = new Date();

      switch (period) {
        case "daily":
          // Last 7 days
          label = "Last 7 Days";
          for (let i = 6; i >= 0; i--) {
            const date = new Date(now);
            date.setDate(date.getDate() - i);
            const revenue = await medicalDB.getDailyRevenue(date);
            data.push({
              name: date.toLocaleDateString("en-IL", {
                month: "short",
                day: "numeric",
              }),
              revenue: revenue.totalRevenue,
              transactions: revenue.totalTransactions,
              date: date.toISOString().split("T")[0],
            });
            total += revenue.totalRevenue;
          }
          break;

        case "weekly":
          // Last 8 weeks
          label = "Last 8 Weeks";
          for (let i = 7; i >= 0; i--) {
            const startDate = new Date(now);
            startDate.setDate(startDate.getDate() - i * 7 - startDate.getDay());
            const endDate = new Date(startDate);
            endDate.setDate(endDate.getDate() + 6);

            const revenue = await medicalDB.getWeeklyRevenue(startDate);
            data.push({
              name: `Week ${startDate.toLocaleDateString("en-IL", {
                month: "short",
                day: "numeric",
              })}`,
              revenue: revenue.totalRevenue,
              transactions: revenue.totalTransactions,
              period: `${startDate.toLocaleDateString(
                "en-IL"
              )} - ${endDate.toLocaleDateString("en-IL")}`,
            });
            total += revenue.totalRevenue;
          }
          break;

        case "monthly":
          // Last 6 months
          label = "Last 6 Months";
          for (let i = 5; i >= 0; i--) {
            const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const revenue = await medicalDB.getMonthlyRevenue(date);
            data.push({
              name: date.toLocaleDateString("en-IL", {
                month: "short",
                year: "numeric",
              }),
              revenue: revenue.totalRevenue,
              transactions: revenue.totalTransactions,
              month: date.toISOString().slice(0, 7),
            });
            total += revenue.totalRevenue;
          }
          break;

        case "yearly":
          // Last 3 years
          label = "Last 3 Years";
          for (let i = 2; i >= 0; i--) {
            const year = now.getFullYear() - i;
            const date = new Date(year, 0, 1);
            const revenue = await medicalDB.getYearlyRevenue(date);
            data.push({
              name: year.toString(),
              revenue: revenue.totalRevenue,
              transactions: revenue.totalTransactions,
              year: year,
            });
            total += revenue.totalRevenue;
          }
          break;
      }

      setChartData(data);
      setTotalRevenue(total);
      setPeriodLabel(label);
    } catch (error) {
      console.error("Error loading revenue data:", error);
      setChartData([]);
      setTotalRevenue(0);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => `₪${value.toFixed(0)}`;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900">{label}</p>
          {data.period && (
            <p className="text-sm text-gray-600">{data.period}</p>
          )}
          <p className="text-blue-600 font-semibold">
            Revenue: {formatCurrency(payload[0].value)}
          </p>
          <p className="text-gray-600 text-sm">
            Transactions: {data.transactions}
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading revenue data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          {chartType === "line" ? (
            <TrendingUp className="h-6 w-6 text-blue-600" />
          ) : (
            <BarChart3 className="h-6 w-6 text-blue-600" />
          )}
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Revenue Trends
            </h3>
            <p className="text-sm text-gray-600">{periodLabel}</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-gray-900">
            {formatCurrency(totalRevenue)}
          </div>
          <div className="text-sm text-gray-600">Total Revenue</div>
        </div>
      </div>

      {/* Chart */}
      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === "line" ? (
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="name"
                stroke="#6b7280"
                fontSize={12}
                tickLine={false}
              />
              <YAxis
                stroke="#6b7280"
                fontSize={12}
                tickLine={false}
                tickFormatter={formatCurrency}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="revenue"
                stroke="#2563eb"
                strokeWidth={3}
                dot={{ fill: "#2563eb", strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: "#2563eb", strokeWidth: 2 }}
              />
            </LineChart>
          ) : (
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="name"
                stroke="#6b7280"
                fontSize={12}
                tickLine={false}
              />
              <YAxis
                stroke="#6b7280"
                fontSize={12}
                tickLine={false}
                tickFormatter={formatCurrency}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="revenue" fill="#2563eb" radius={[4, 4, 0, 0]} />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>

      {/* Summary Stats */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200">
        <div className="text-center">
          <div className="text-lg font-semibold text-gray-900">
            {chartData.length}
          </div>
          <div className="text-sm text-gray-600">Periods</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-semibold text-gray-900">
            {chartData.reduce((sum, item) => sum + item.transactions, 0)}
          </div>
          <div className="text-sm text-gray-600">Total Transactions</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-semibold text-gray-900">
            {chartData.length > 0
              ? formatCurrency(totalRevenue / chartData.length)
              : "₪0"}
          </div>
          <div className="text-sm text-gray-600">Average per Period</div>
        </div>
      </div>
    </div>
  );
};

export default RevenueChart;
